import React, { useEffect, useMemo, useRef, useState } from "react";
import { CrewPilotModuleShell } from "./CrewPilotModuleShell";
import { HubPrimaryAction } from "./shared/HubPrimaryAction";
import { CmdIcon } from "./shared/CmdIcon";
import { DesktopKpiStrip } from "./shared/DesktopKpiStrip";
import { PAGE_LAYOUT } from "../layout/pageLayout";
import { supabase } from "../supabase";
import { usePermissions } from "../hooks/usePermissions";
import { loadScopedActiveProjects } from "../helpers/operationalQueryAdapters";
import { getActiveClients, getArchivedClients, isClientArchived } from "../helpers/clientVisibility";
import { shouldSkipSupabase } from "../helpers/guestMode";
import "./clientsDesktopHub.css";

const PAGE_SIZE = 8;

function initials(name) {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  return `${parts[0][0]}${parts.length > 1 ? parts[parts.length - 1][0] : ""}`.toUpperCase();
}

const addressOf = (c) => [c?.address, c?.city].filter(Boolean).join(", ");

function matches(client, term) {
  if (!term) return true;
  return [client.client_name, client.contact_name, client.phone, client.email, client.address, client.city].some((v) =>
    String(v || "").toLowerCase().includes(term),
  );
}

function recency(client) {
  const t = new Date(client.updated_at || client.created_at || 0).getTime();
  return Number.isFinite(t) ? t : 0;
}

/**
 * Desktop Clients landing (concept CP-DSK-CLI-LAND-v01): KPI strip, status
 * tabs with counts, sortable card grid with pagination. One tap on a card
 * opens that client's workspace; there is no separate preview panel (it
 * repeated the workspace and stacked under the grid on tablets). Status is
 * Active / Archived only (the product has no "On Hold" state).
 */
export function ClientsDesktopHub({
  allClients,
  canManageArchive,
  onBack,
  onNew,
  onOpenClient,
  onArchive,
  onRestore,
}) {
  const { actor, can, CAPABILITY } = usePermissions();
  const [status, setStatus] = useState("all");
  const [term, setTerm] = useState("");
  const [sort, setSort] = useState("recent");
  const [layout, setLayout] = useState("grid");
  const [page, setPage] = useState(1);
  const [projects, setProjects] = useState([]);
  const [menuFor, setMenuFor] = useState("");
  const menuRef = useRef(null);

  const active = useMemo(() => getActiveClients(allClients), [allClients]);
  const archived = useMemo(() => (canManageArchive ? getArchivedClients(allClients) : []), [allClients, canManageArchive]);

  const tabs = [
    { id: "all", label: "All Clients", count: active.length + archived.length },
    { id: "active", label: "Active", count: active.length },
    ...(canManageArchive ? [{ id: "archived", label: "Archived", count: archived.length }] : []),
  ];

  const filtered = useMemo(() => {
    const pool = status === "active" ? active : status === "archived" ? archived : [...active, ...archived];
    const needle = term.toLowerCase().trim();
    const rows = pool.filter((c) => matches(c, needle));
    rows.sort((a, b) =>
      sort === "name"
        ? String(a.client_name || "").localeCompare(String(b.client_name || ""))
        : recency(b) - recency(a),
    );
    return rows;
  }, [active, archived, status, term, sort]);

  const pages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, pages);
  const pageRows = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  useEffect(() => setPage(1), [status, term, sort]);

  // Project counts per client (real rows only).
  useEffect(() => {
    if (!actor || shouldSkipSupabase(actor)) return undefined;
    let cancelled = false;
    loadScopedActiveProjects(supabase, actor, {}, "project_id, project_name, client_id, status, is_active, updated_at", { activeOnly: true })
      .then((rows) => !cancelled && setProjects(rows || []))
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [actor]);

  const byClient = useMemo(() => {
    const map = new Map();
    projects.forEach((p) => {
      if (!p.client_id) return;
      const list = map.get(p.client_id) || [];
      list.push(p);
      map.set(p.client_id, list);
    });
    return map;
  }, [projects]);

  useEffect(() => {
    if (!menuFor) return undefined;
    const close = (e) => {
      if (e.type === "keydown" ? e.key === "Escape" : !menuRef.current?.contains(e.target)) setMenuFor("");
    };
    document.addEventListener("pointerdown", close);
    document.addEventListener("keydown", close);
    return () => {
      document.removeEventListener("pointerdown", close);
      document.removeEventListener("keydown", close);
    };
  }, [menuFor]);

  const canCreate = can(CAPABILITY.CLIENT_CREATE);

  return (
    <CrewPilotModuleShell
      title="Clients"
      subtitle="Manage your client relationships"
      onBack={onBack}
      layout={PAGE_LAYOUT.COMMAND}
      primary
      compact
      headerActions={
        <div className="cdh-actions">
          <label className="cdh-search">
            <CmdIcon name="search" />
            <input type="text" value={term} onChange={(e) => setTerm(e.target.value)} placeholder="Search clients by name, email, or address…" aria-label="Search clients" />
          </label>
          {canCreate ? (
            <HubPrimaryAction onClick={onNew} label="New Client" ariaLabel="+ New Client" />
          ) : null}
        </div>
      }
    >
      <div className="cdh" data-testid="clients-desktop-hub">
        <div className="cdh-main">
          <DesktopKpiStrip
            ariaLabel="Clients summary"
            tiles={[
              { key: "total", value: active.length + archived.length, label: "Total Clients", tone: "gold" },
              { key: "active", value: active.length, label: "Active Clients", tone: "blue" },
              canManageArchive ? { key: "archived", value: archived.length, label: "Archived", tone: "green" } : null,
            ]}
          />

          <div className="cdh-bar">
            <div className="cdh-tabs" role="tablist" aria-label="Client status">
              {tabs.map((t) => (
                <button key={t.id} type="button" role="tab" aria-selected={status === t.id} className={`cdh-tab${status === t.id ? " is-on" : ""}`} onClick={() => setStatus(t.id)}>
                  {t.label} ({t.count})
                </button>
              ))}
            </div>
            <div className="cdh-view" role="group" aria-label="Layout">
              <button type="button" aria-pressed={layout === "grid"} className={layout === "grid" ? "is-on" : ""} onClick={() => setLayout("grid")} aria-label="Grid view"><CmdIcon name="grid" /></button>
              <button type="button" aria-pressed={layout === "list"} className={layout === "list" ? "is-on" : ""} onClick={() => setLayout("list")} aria-label="List view"><CmdIcon name="list" /></button>
            </div>
            <label className="cdh-sort">
              Sort:
              <select value={sort} onChange={(e) => setSort(e.target.value)}>
                <option value="recent">Recently Updated</option>
                <option value="name">Name (A–Z)</option>
              </select>
            </label>
          </div>

          {pageRows.length === 0 ? (
            <p className="cdh-empty">
              {term ? "No clients match your search." : status === "archived" ? "No archived clients." : "No clients found."}
            </p>
          ) : (
            <div className={`cdh-grid cdh-grid--${layout}`}>
              {pageRows.map((client) => {
                const isArchived = isClientArchived(client);
                const list = byClient.get(client.client_id) || [];
                return (
                  <article
                    key={client.client_id}
                    className={`cdh-card${isArchived ? " is-archived" : ""}`}
                    data-testid="client-card"
                  >
                    {/* The whole card is the link: this button's ::after stretches over the card. */}
                    <button type="button" className="cdh-card__main" onClick={() => onOpenClient(client.client_id)} aria-label={`Open ${client.client_name || "client"}`}>
                      <span className="cdh-avatar" aria-hidden="true">{initials(client.client_name)}</span>
                      <span className="cdh-card__text">
                        <span className={`pj-status${isArchived ? " pj-status--archived" : ""}`}>{isArchived ? "Archived" : "Active"}</span>
                        <span className="cdh-card__name">{client.client_name || "Unnamed Client"}</span>
                        {client.contact_name ? <span className="cdh-line"><CmdIcon name="user" />{client.contact_name}</span> : null}
                        {client.phone ? <span className="cdh-line"><CmdIcon name="phone" />{client.phone}</span> : null}
                        {client.email ? <span className="cdh-line"><CmdIcon name="mail" />{client.email}</span> : null}
                        {addressOf(client) ? <span className="cdh-line"><CmdIcon name="pin" />{addressOf(client)}</span> : null}
                      </span>
                    </button>
                    {canManageArchive ? (
                      <div className="cdh-card__side" ref={menuFor === client.client_id ? menuRef : null}>
                        <button type="button" className="cdh-kebab" aria-haspopup="menu" aria-expanded={menuFor === client.client_id} aria-label={`More actions for ${client.client_name || "client"}`} onClick={() => setMenuFor(menuFor === client.client_id ? "" : client.client_id)}>⋮</button>
                        {menuFor === client.client_id ? (
                          <div className="cdh-menu" role="menu">
                            {isArchived ? (
                              <button type="button" role="menuitem" onClick={() => { setMenuFor(""); onRestore(client.client_id); }}>Restore</button>
                            ) : (
                              <button type="button" role="menuitem" onClick={() => { setMenuFor(""); onArchive(client.client_id); }}>Archive</button>
                            )}
                          </div>
                        ) : null}
                      </div>
                    ) : null}
                    <div className="cdh-card__foot">
                      <span className="cdh-card__count"><CmdIcon name="folder" />{list.length} {list.length === 1 ? "project" : "projects"}</span>
                      <span className="cdh-card__projects">
                        {list.slice(0, 3).map((p) => (
                          <span key={p.project_id} className="cdh-ptile" title={p.project_name}>{initials(p.project_name)}</span>
                        ))}
                      </span>
                    </div>
                  </article>
                );
              })}
            </div>
          )}

          {filtered.length > PAGE_SIZE ? (
            <nav className="cdh-pager" aria-label="Pagination">
              <div className="cdh-pager__pages">
                <button type="button" onClick={() => setPage(Math.max(1, safePage - 1))} disabled={safePage === 1} aria-label="Previous page">‹</button>
                {Array.from({ length: pages }, (_, i) => i + 1).map((n) => (
                  <button key={n} type="button" className={n === safePage ? "is-on" : ""} aria-current={n === safePage ? "page" : undefined} onClick={() => setPage(n)}>{n}</button>
                ))}
                <button type="button" onClick={() => setPage(Math.min(pages, safePage + 1))} disabled={safePage === pages} aria-label="Next page">›</button>
              </div>
              <span>Showing {(safePage - 1) * PAGE_SIZE + 1}–{Math.min(filtered.length, safePage * PAGE_SIZE)} of {filtered.length} clients</span>
            </nav>
          ) : null}
        </div>

      </div>
    </CrewPilotModuleShell>
  );
}
